import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { EXPORT_SECTIONS, EXPORT_MODES } from '../model/exportColumns';
import { useExportBuilder } from '../hooks/useExportBuilder';

const COLORS = {
  primary: '#0d6aee',
  border: '#dbe7f3',
  surface: '#f8fbff',
  textMain: '#0f172a',
  textMuted: '#475569',
};

const sectionPaperSx = {
  p: { xs: 2, md: 3 },
  borderRadius: 3,
  border: `1px solid ${COLORS.border}`,
  background: COLORS.surface,
};

export default function ExportBuilder() {
  const {
    mode, setMode,
    year, setYear,
    selection,
    presets,
    presetName, setPresetName,
    downloading,
    error, setError,
    success, setSuccess,
    toggleField,
    toggleSection,
    isSectionChecked,
    isSectionIndeterminate,
    handleSavePreset,
    handleLoadPreset,
    handleDeletePreset,
    handleDownload,
  } = useExportBuilder();

  const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Box sx={{ width: '100%', maxWidth: 1080 }}>

        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 2,
            borderRadius: 4,
            border: `1px solid ${COLORS.border}`,
            background: 'linear-gradient(135deg, #f8fbff 0%, #edf6ff 55%, #f5fbff 100%)',
            boxShadow: '0 20px 45px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Typography variant="overline" sx={{ color: '#1d4ed8', letterSpacing: 1.4 }}>
            Exportación avanzada
          </Typography>
          <Typography variant="h4" sx={{ color: COLORS.textMain, fontWeight: 600, lineHeight: 1.1, mb: 1 }}>
            Generador de Excel
          </Typography>
          <Typography sx={{ color: COLORS.textMuted }}>
            Selecciona las columnas, el modo de comparación y el año para generar la base de datos a medida.
          </Typography>
        </Paper>

        <Stack spacing={2}>

          {/* Modo y año */}
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="h6" sx={{ color: COLORS.textMain, fontWeight: 700, mb: 2 }}>
              Configuración general
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }}>
              <Box>
                <Typography variant="caption" sx={{ color: COLORS.textMuted, fontWeight: 600, display: 'block', mb: 1 }}>
                  MODO DE EXPORTACIÓN
                </Typography>
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={(_, val) => val && setMode(val)}
                  size="small"
                >
                  {EXPORT_MODES.map((m) => (
                    <ToggleButton
                      key={m.value}
                      value={m.value}
                      sx={{
                        px: 2,
                        textTransform: 'none',
                        '&.Mui-selected': { backgroundColor: '#dbeafe', color: COLORS.primary, fontWeight: 700 },
                      }}
                    >
                      {m.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: COLORS.textMuted, fontWeight: 600, display: 'block', mb: 1 }}>
                  AÑO
                </Typography>
                <TextField
                  select
                  size="small"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  sx={{ minWidth: 120 }}
                >
                  {yearOptions.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </TextField>
              </Box>
            </Stack>
          </Paper>

          {/* Presets */}
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="h6" sx={{ color: COLORS.textMain, fontWeight: 700, mb: 2 }}>
              Configuraciones guardadas
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap">
              <TextField
                size="small"
                placeholder="Nombre del preset..."
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                sx={{ minWidth: 200 }}
              />
              <Button
                variant="outlined"
                startIcon={<BookmarkBorderIcon />}
                onClick={handleSavePreset}
                disabled={!presetName.trim()}
                size="small"
              >
                Guardar actual
              </Button>
            </Stack>

            {presets.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
                {presets.map((preset) => (
                  <Chip
                    key={preset.name}
                    icon={<BookmarkIcon fontSize="small" />}
                    label={preset.name}
                    onClick={() => handleLoadPreset(preset)}
                    onDelete={() => handleDeletePreset(preset.name)}
                    deleteIcon={<DeleteOutlineIcon />}
                    variant="outlined"
                    sx={{ borderColor: COLORS.border, '&:hover': { borderColor: COLORS.primary } }}
                  />
                ))}
              </Stack>
            )}
          </Paper>

          {/* Columnas por sección */}
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="h6" sx={{ color: COLORS.textMain, fontWeight: 700, mb: 2 }}>
              Columnas a incluir
            </Typography>
            <Stack spacing={2}>
              {EXPORT_SECTIONS.map((section, idx) => (
                <Box key={section.id}>
                  {idx > 0 && <Divider sx={{ mb: 2 }} />}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <FormControlLabel
                      label={
                        <Typography sx={{ fontWeight: 700, color: COLORS.textMain }}>
                          {section.label}
                        </Typography>
                      }
                      control={
                        <Checkbox
                          checked={isSectionChecked(section.id)}
                          indeterminate={isSectionIndeterminate(section.id)}
                          onChange={() => toggleSection(section.id)}
                          sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }}
                        />
                      }
                    />
                    {section.prePost && (
                      <Chip
                        label={mode === 'comparison' ? 'PRE + POST' : mode.toUpperCase()}
                        size="small"
                        sx={{ backgroundColor: '#dbeafe', color: COLORS.primary, fontWeight: 600, fontSize: 11 }}
                      />
                    )}
                  </Stack>
                  <Grid container spacing={0.5} sx={{ pl: 4 }}>
                    {section.fields.map((field) => (
                      <Grid item xs={12} sm={6} md={4} key={field.id}>
                        <FormControlLabel
                          label={
                            <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
                              {field.label}
                            </Typography>
                          }
                          control={
                            <Checkbox
                              size="small"
                              checked={selection[section.id]?.[field.id] ?? false}
                              onChange={() => toggleField(section.id, field.id)}
                              sx={{ '&.Mui-checked': { color: COLORS.primary } }}
                            />
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </Stack>
          </Paper>

          {/* Error y botón */}
          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

          <Button
            variant="contained"
            size="large"
            startIcon={<FileDownloadIcon />}
            onClick={handleDownload}
            disabled={downloading}
            sx={{ borderRadius: 3, py: 1.5, fontWeight: 700, backgroundColor: COLORS.primary }}
          >
            {downloading ? 'Generando Excel...' : 'Descargar Excel'}
          </Button>

        </Stack>

        <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}>
          <Alert severity="success" onClose={() => setSuccess(false)}>
            Excel generado correctamente.
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
